import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import store, { IAppState } from "../../state/Store";
import Datastore from "../../communication/Datastore";
import State from "../../types/core/State";
import Country from "../../types/core/Country";
import { REHYDRATE } from "redux-persist";
import Timezone from "../../types/core/Timezone";

// Define the type for our state store entry
type GeographyState = {
    states: Record<number, State[]>;
    countries: Country[];
    timezones: Timezone[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: GeographyState = {
    states: null,
    countries: [],
    timezones: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const geographySlice = createSlice({
    name: "geography",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    states: rawData?.payload?.geography?.states,
                    countries: rawData?.payload?.geography?.countries,
                    timezones: rawData?.payload?.geography?.timezones,
                    status:
                        !rawData?.payload?.geography?.states ||
                        !rawData?.payload?.geography?.countries ||
                        !rawData?.payload?.geography?.countries.length ||
                        !rawData?.payload?.geography?.timezones ||
                        !rawData?.payload?.geography?.timezones.length
                            ? "idle"
                            : rawData?.payload?.geography?.status,
                    error: null,
                };
            })
            .addCase(fetchGeography.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchGeography.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.states = action.payload[0];
                state.countries = action.payload[1];
                state.timezones = action.payload[2];
            })
            .addCase(fetchGeography.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

// This is a thunk, they are used to load the state
const fetchGeography = createAsyncThunk(
    "geography/fetchGeography",
    () =>
        Promise.all([
            Datastore.getType<Record<number, State[]>>("api/Geography/States"),
            Datastore.getType<Country[]>("api/Geography/Countries"),
            Datastore.getType<Timezone[]>("api/Geography/Timezones"),
        ]),
    {
        condition: (_, { getState }) =>
            (getState() as IAppState).db.geography.status === "idle",
    },
);

export const initialiseGeographyDetails = async (): Promise<void> => {
    await store.dispatch(fetchGeography());
};

// We have to export the reducer as this is added to the state store
export default geographySlice.reducer;
